// src/lib/supabase.ts
import { createClient } from '@supabase/supabase-js';
import { slugify } from '../utils/slugify'; // Import slugify function

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // REMOVE or Comment out the line below so email is ALWAYS required
    // skipEmailConfirmation: import.meta.env.DEV 
  }
});

// --- CHANGE 1: Added slug to Listing type ---
export type Listing = {
  id: string;
  user_id: string;
  brand: string;
  model: string;
  year: number;
  mileage: number;
  fuel_type: string;
  transmission: string;
  color: string;
  price: number;
  description: string;
  location: string;
  condition: string;
  contact_phone: string;
  contact_email: string;
  images: string[];
  features: string[];      // <--- ADDED
  negotiable: boolean;     // <--- ADDED
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  approved_at?: string;
  slug: string;
};
export interface QuoteRequest {
  id?: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  city: string;
  message?: string;
  car_model_id?: string;
  car_model_name?: string;
  car_brand_name?: string;
  status?: 'pending' | 'processed' | 'rejected';
  created_at?: string;
}

// NEW: Function to submit a quote request
export const submitQuoteRequest = async (quote: QuoteRequest) => {
  const { data, error } = await supabase
    .from('quote_requests')
    .insert([quote])
    .select()
    .single();

  if (error) throw error;
  return data;
};
export type Banner = {
  id: string;
  page: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'all';
  image_url: string;
  link_url?: string;
  is_active: boolean;
  display_order: number;
  start_date?: string;
  end_date?: string;
  created_at: string;
};

// --- CHANGE 2: Added fetchListingBySlug function ---
export async function fetchListingBySlug(slug: string, userId?: string): Promise<Listing | null> {
  try {
    let query = supabase
      .from('listings')
      .select('*')
      .eq('slug', slug);

    // If a userId is provided, use an 'or' filter.
    // This allows fetching if status is 'approved' OR if user is the owner.
    if (userId) {
      query = query.or(`status.eq.approved,user_id.eq.${userId}`);
    } else {
      // If no user is logged in, only show approved listings.
      query = query.eq('status', 'approved');
    }

    const { data, error } = await query.single();

    // Handle specific PGRST116 error (no rows returned) gracefully.
    if (error) {
      if (error.code === 'PGRST116') {
        return null; // This is an expected case, not a true error.
      }
      console.error('Error fetching listing by slug:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Unexpected error fetching listing by slug:', error);
    return null;
  }
}

// --- CHANGE 3: Updated fetchListingById to include slug ---
export async function fetchListingById(id: string, userId?: string): Promise<Listing | null> {
  try {
    let query = supabase
      .from('listings')
      .select('*')
      .eq('id', id);

    // If a userId is provided, use an 'or' filter.
    // This allows fetching if status is 'approved' OR if user is the owner.
    if (userId) {
      query = query.or(`status.eq.approved,user_id.eq.${userId}`);
    } else {
      // If no user is logged in, only show approved listings.
      query = query.eq('status', 'approved');
    }

    const { data, error } = await query.single();

    // Handle specific PGRST116 error (no rows returned) gracefully.
    if (error) {
      if (error.code === 'PGRST116') {
        return null; // This is an expected case, not a true error.
      }
      console.error('Error fetching listing:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Unexpected error fetching listing:', error);
    return null;
  }
}

// --- CHANGE 4: Updated createListing to generate slug ---
export async function createListing(listingData: Omit<Listing, 'id' | 'created_at' | 'approved_at' | 'slug'>): Promise<Listing | null> {
  try {
    // Generate slug from brand, model, and year
    const slug = slugify(`${listingData.brand} ${listingData.model} ${listingData.year}`);
    
    const { data, error } = await supabase
      .from('listings')
      .insert({
        ...listingData,
        slug, // Add the slug to the data
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating listing:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in createListing:', error);
    return null;
  }
}

// --- CHANGE 5: Updated updateListing to handle slug ---
export async function updateListing(id: string, updates: Partial<Listing>): Promise<{ error: Error | null }> {
  try {
    // If brand, model, or year is updated, regenerate the slug
    let updateData = { ...updates };
    if (updates.brand || updates.model || updates.year) {
      const currentListing = await fetchListingById(id);
      if (currentListing) {
        const brand = updates.brand || currentListing.brand;
        const model = updates.model || currentListing.model;
        const year = updates.year || currentListing.year;
        updateData.slug = slugify(`${brand} ${model} ${year}`);
      }
    }
    
    const { error } = await supabase
      .from('listings')
      .update(updateData)
      .eq('id', id);
    
    if (error) {
      console.error('Error updating listing:', error);
      return { error: error as Error };
    }
    
    return { error: null };
  } catch (error) {
    console.error('Unexpected error updating listing:', error);
    return { error: error as Error };
  }
}

// --- CHANGE 6: Improved deleteListing function ---
export async function deleteListing(id: string): Promise<{ error: Error | null }> {
  try {
    const { error } = await supabase
      .from('listings')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error('Error deleting listing:', error);
      return { error: error as Error };
    }
    
    return { error: null };
  } catch (error) {
    console.error('Unexpected error deleting listing:', error);
    return { error: error as Error };
  }
}

// --- CHANGE 7: Improved fetchApprovedListings function ---
export async function fetchApprovedListings(): Promise<Listing[]> {
  try {
    const { data, error } = await supabase
      .from('listings')
      .select('*')
      .eq('status', 'approved')
      .order('approved_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching approved listings:', error);
      return [];
    }
    
    return data || [];
  } catch (error) {
    console.error('Unexpected error fetching approved listings:', error);
    return [];
  }
}

// --- CHANGE 8: Function to generate slugs for existing listings ---
export async function generateSlugsForExistingListings(): Promise<{ success: boolean; count: number; error?: string }> {
  try {
    // Fetch all listings without slugs
    const { data: listings, error } = await supabase
      .from('listings')
      .select('id, brand, model, year')
      .is('slug', 'null');

    if (error) {
      console.error('Error fetching listings without slugs:', error);
      return { success: false, count: 0, error: error.message };
    }

    // Generate and update slugs for each listing
    let successCount = 0;
    for (const listing of listings || []) {
      const slug = slugify(`${listing.brand} ${listing.model} ${listing.year}`);
      
      const { error: updateError } = await supabase
        .from('listings')
        .update({ slug })
        .eq('id', listing.id);

      if (updateError) {
        console.error(`Error updating slug for listing ${listing.id}:`, updateError);
      } else {
        successCount++;
      }
    }

    return { success: true, count: successCount };
  } catch (error) {
    console.error('Error in generateSlugsForExistingListings:', error);
    return { success: false, count: 0, error: (error as Error).message };
  }
}

// --- CHANGE 9: Improved fetchBannersForPage function ---
export async function fetchBannersForPage(page: string): Promise<Banner[]> {
  try {
    const now = new Date();
    const { data, error } = await supabase
      .from('banners')
      .select('*')
      .eq('page', page)
      .eq('is_active', true)
      .or('position.eq.all')
      .order('display_order', { ascending: true });
    
    if (error) {
      console.error('Error fetching banners:', error);
      return [];
    }
    
    // Filter by date range if specified
    return (data || []).filter(banner => {
      if (banner.start_date && new Date(banner.start_date) > now) return false;
      if (banner.end_date && new Date(banner.end_date) < now) return false;
      return true;
    });
  } catch (error) {
    console.error('Unexpected error fetching banners:', error);
    return [];
  }
}